import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'react-feather';
import DeliveryPartnersField from './DeliveryPartnersField';

const STORE_TYPES = ['Anchor', 'Vanilla', 'Kiosk', 'Other'];
const CURRENT_STATUSES = ['Trading', 'Fitout', 'Closed Temporarily', 'Closed Permanently'];

/**
 * Unit information section — used by both AddTenant and EditTenant.
 *
 * Props: same shape as BrandSection. `readOnly` typically lists
 * `tenant_category_id` + `tenant_subcategory_id` on Edit so they render
 * as KV display (matching the V2 profile sidebar behaviour).
 */
const UnitSection = ({ form, errors = {}, onChange, mode = 'add', lookups, readOnly = [] }) => {
    const { t } = useTranslation();
    const ro = (name) => readOnly.includes(name);
    const v = (name) => form[name] ?? '';

    return (
        <section className="v3-card v3-tenant-section" id="section-unit">
            <header className="v3-tenant-section-head">
                <h3>{t('Unit information')}</h3>
                <span className="v3-label">{mode === 'add' ? t('Step 2 of 3') : t('Unit details')}</span>
            </header>

            <div className="v3-sadmin-form-grid">
                <label className="v3-field">
                    <span className="v3-label">{t('Tenant code *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_code')}
                        onChange={(e) => onChange('tenant_code', e.target.value)}
                        placeholder={t('Internal code')}
                        disabled={ro('tenant_code') || mode === 'edit'} // tcode is checked for uniqueness on create; not editable later
                    />
                    {errors.tenant_code && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_code}</span>
                    )}
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('Unit code *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_unit_code')}
                        onChange={(e) => onChange('tenant_unit_code', e.target.value)}
                        placeholder={t('Mall unit number')}
                        disabled={ro('tenant_unit_code')}
                    />
                    {errors.tenant_unit_code && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_unit_code}</span>
                    )}
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('Store type *')}</span>
                    <div className="v3-select-wrap">
                        <select
                            className="v3-input v3-select"
                            value={v('tenant_store_type')}
                            onChange={(e) => onChange('tenant_store_type', e.target.value)}
                            disabled={ro('tenant_store_type')}
                        >
                            <option value="">{t('Select store type…')}</option>
                            {STORE_TYPES.map((s) => (
                                <option key={s} value={s}>{t(s)}</option>
                            ))}
                        </select>
                    </div>
                    {errors.tenant_store_type && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_store_type}</span>
                    )}
                    {v('tenant_store_type') === 'Other' && (
                        <input
                            className="v3-input"
                            style={{ marginTop: 6 }}
                            value={v('other_store_type')}
                            onChange={(e) => onChange('other_store_type', e.target.value)}
                            placeholder={t('Specify other store type')}
                        />
                    )}
                    {errors.other_store_type && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.other_store_type}</span>
                    )}
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('Current status *')}</span>
                    <div className="v3-select-wrap">
                        <select
                            className="v3-input v3-select"
                            value={v('tenant_current_status')}
                            onChange={(e) => onChange('tenant_current_status', e.target.value)}
                            disabled={ro('tenant_current_status')}
                        >
                            <option value="">{t('Select status…')}</option>
                            {CURRENT_STATUSES.map((s) => (
                                <option key={s} value={s}>{t(s)}</option>
                            ))}
                        </select>
                    </div>
                    {errors.tenant_current_status && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_current_status}</span>
                    )}
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('Carpet area (sq ft) *')}</span>
                    <input
                        className="v3-input"
                        type="number"
                        step="0.001"
                        value={v('tenant_carpet_area')}
                        onChange={(e) => onChange('tenant_carpet_area', e.target.value)}
                        placeholder="0"
                        disabled={ro('tenant_carpet_area')}
                    />
                    {errors.tenant_carpet_area && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_carpet_area}</span>
                    )}
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('Chargeable area (sq ft) *')}</span>
                    <input
                        className="v3-input"
                        type="number"
                        step="0.001"
                        value={v('tenant_chargable_area')}
                        onChange={(e) => onChange('tenant_chargable_area', e.target.value)}
                        placeholder="0"
                        disabled={ro('tenant_chargable_area')}
                    />
                    {errors.tenant_chargable_area && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_chargable_area}</span>
                    )}
                </label>

                <label className="v3-field">
                    <span className="v3-label">{t('Floor *')}</span>
                    <div className="v3-select-wrap">
                        <select
                            className="v3-input v3-select"
                            value={v('tenant_floor')}
                            onChange={(e) => onChange('tenant_floor', e.target.value)}
                            disabled={ro('tenant_floor') || !v('tenant_property_id')}
                        >
                            <option value="">
                                {v('tenant_property_id') ? t('Select floor…') : t('Pick a property first')}
                            </option>
                            {(lookups?.floors || []).map((floor) => (
                                <option
                                    key={floor.floor_id?.floor_name || floor.floor_id || floor._id}
                                    value={floor.floor_id?.floor_name || floor.floor_name || ''}
                                >
                                    {floor.floor_id?.floor_name || floor.floor_name || ''}
                                </option>
                            ))}
                        </select>
                    </div>
                    {errors.tenant_floor && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_floor}</span>
                    )}
                </label>
                <label className="v3-field">
                    <span className="v3-label">{t('Location *')}</span>
                    <input
                        className="v3-input"
                        value={v('tenant_location')}
                        onChange={(e) => onChange('tenant_location', e.target.value)}
                        placeholder={t('In-mall location / wing')}
                        disabled={ro('tenant_location')}
                    />
                    {errors.tenant_location && (
                        <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_location}</span>
                    )}
                </label>

                {ro('tenant_category_id') ? (
                    <div className="v3-sadmin-kv-item">
                        <span className="v3-sadmin-kv-label">{t('Category')}</span>
                        <span className="v3-sadmin-kv-value">
                            {(lookups?.categories || []).find((c) => c._id === v('tenant_category_id'))?.category_name || '—'}
                        </span>
                    </div>
                ) : (
                    <label className="v3-field">
                        <span className="v3-label">{t('Category *')}</span>
                        <div className="v3-select-wrap">
                            <select
                                className="v3-input v3-select"
                                value={v('tenant_category_id')}
                                onChange={(e) => onChange('tenant_category_id', e.target.value)}
                            >
                                <option value="">{t('Select category…')}</option>
                                {(lookups?.categories || []).map((c) => (
                                    <option key={c._id} value={c._id}>{c.category_name}</option>
                                ))}
                            </select>
                        </div>
                        {errors.tenant_category_id && (
                            <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_category_id}</span>
                        )}
                    </label>
                )}

                {ro('tenant_subcategory_id') ? (
                    <div className="v3-sadmin-kv-item">
                        <span className="v3-sadmin-kv-label">{t('Subcategory')}</span>
                        <span className="v3-sadmin-kv-value">
                            {(lookups?.subcategories || []).find((s) => s._id === v('tenant_subcategory_id'))?.subcategory_name || '—'}
                        </span>
                    </div>
                ) : (
                    <label className="v3-field">
                        <span className="v3-label">{t('Subcategory *')}</span>
                        <div className="v3-select-wrap">
                            <select
                                className="v3-input v3-select"
                                value={v('tenant_subcategory_id')}
                                onChange={(e) => onChange('tenant_subcategory_id', e.target.value)}
                                disabled={!v('tenant_category_id')}
                            >
                                <option value="">
                                    {v('tenant_category_id') ? t('Select subcategory…') : t('Pick a category first')}
                                </option>
                                {(lookups?.subcategories || []).map((s) => (
                                    <option key={s._id} value={s._id}>{s.subcategory_name}</option>
                                ))}
                            </select>
                        </div>
                        {errors.tenant_subcategory_id && (
                            <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.tenant_subcategory_id}</span>
                        )}
                    </label>
                )}
            </div>

            <label className="v3-field" style={{ marginTop: 16 }}>
                <span className="v3-label">{t('Delivery partners')}</span>
                <DeliveryPartnersField
                    value={form.deliveryPartnersId || []}
                    options={lookups?.deliveryPartners || []}
                    onChange={(ids) => onChange('deliveryPartnersId', ids)}
                />
            </label>
        </section>
    );
};

export const UNIT_FIELDS = {
    required: [
        'tenant_code', 'tenant_unit_code', 'tenant_store_type', 'tenant_current_status',
        'tenant_carpet_area', 'tenant_chargable_area', 'tenant_floor', 'tenant_location',
        'tenant_category_id', 'tenant_subcategory_id',
    ],
    all: [
        'tenant_code', 'tenant_unit_code', 'tenant_store_type', 'other_store_type',
        'tenant_current_status', 'tenant_carpet_area', 'tenant_chargable_area',
        'tenant_floor', 'tenant_location', 'tenant_category_id', 'tenant_subcategory_id',
        'deliveryPartnersId',
    ],
};

export default UnitSection;
